import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';

import { openMatchVideo } from './instatVideo';
import { useInstatDesign } from './theme/instatDesign';
import { Card, Pill, SegmentedControl } from './ui';

function formatDate(d) {
  if (!d) return '—';
  const iso = d.slice(0, 10);
  const [y, m, day] = iso.split('-');
  if (!y) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[Number(m) - 1]} ${y.slice(2)}`;
}

function shortLabel(key) {
  switch (key) {
    case 'full_game':         return 'Match';
    case 'all_actions':       return 'Actions';
    case 'offensive_actions': return 'Off';
    case 'defensive_actions': return 'Def';
    default:                  return key;
  }
}

// Color-coded dot for W/L + score delta
function ResultDot({ win }) {
  const d = useInstatDesign();
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: win ? d.colors.success : d.colors.danger,
        boxShadow: `0 0 0 3px ${win ? d.colors.successSoft : d.colors.dangerSoft}`,
      }}
    />
  );
}

function SortHeader({ children, active, dir, onClick, align = 'left', minWidth, sx = {} }) {
  const d = useInstatDesign();
  return (
    <TableCell
      align={align}
      onClick={onClick}
      sx={{
        ...d.typo.micro,
        color: active ? d.colors.text : d.colors.textSecondary,
        bgcolor: d.colors.canvasAlt,
        borderBottom: `1px solid ${d.colors.divider}`,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        cursor: 'pointer',
        userSelect: 'none',
        minWidth,
        py: 1.25,
        transition: `color ${d.motion.fast}`,
        '&:hover': { color: d.colors.text },
        ...sx,
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {children}
        {active && (dir === 'asc'
          ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
          : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14 }} />)}
      </Box>
    </TableCell>
  );
}

function VideoPillButton({ label, onClick }) {
  const d = useInstatDesign();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        height: 24,
        px: 1,
        bgcolor: d.accentSoft,
        color: d.accent,
        borderRadius: `${d.radius.pill}px`,
        fontSize: 11.5,
        fontWeight: 600,
        cursor: 'pointer',
        transition: `transform ${d.motion.fast}, background-color ${d.motion.fast}`,
        '&:hover': { transform: 'translateY(-1px)', bgcolor: d.accentBorder, color: '#fff' },
      }}
    >
      <PlayCircleFilledRoundedIcon sx={{ fontSize: 14 }} />
      {label}
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function GamesTab({ playerId, playerName, games, playerContext = null }) {
  const d = useInstatDesign();
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const tournamentDict = useMemo(() => {
    const m = {};
    for (const t of games?.tournaments || []) m[t.id] = t.name;
    return m;
  }, [games]);

  const sortedFiltered = useMemo(() => {
    if (!games?.matches) return [];
    let rows = games.matches;
    if (tournamentFilter) rows = rows.filter((m) => m.tournament_id === Number(tournamentFilter));

    const cmp = (a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'date':     return dir * String(a.date || '').localeCompare(b.date || '');
        case 'diff': {
          const da = (a.team?.score ?? 0) - (a.opp_team?.score ?? 0);
          const db = (b.team?.score ?? 0) - (b.opp_team?.score ?? 0);
          return dir * (da - db);
        }
        case 'tournament': return dir * ((tournamentDict[a.tournament_id] || '').localeCompare(tournamentDict[b.tournament_id] || ''));
        case 'opp':      return dir * ((a.opp_team?.name || '').localeCompare(b.opp_team?.name || ''));
        default:         return 0;
      }
    };
    return [...rows].sort(cmp);
  }, [games, tournamentFilter, sortKey, sortDir, tournamentDict]);

  const handleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (!games) {
    return (
      <Card>
        <Typography sx={{ ...d.typo.caption }}>No matches loaded.</Typography>
      </Card>
    );
  }

  const playlistTypes = games.playlistTypes || [];
  const useSegmented = (games.tournaments?.length || 0) <= 3;

  const tournamentOptions = [
    { value: '', label: 'All', count: games.matches.length },
    ...(games.tournaments || []).map((t) => ({
      value: String(t.id),
      label: t.name,
      count: games.matches.filter((m) => m.tournament_id === t.id).length,
    })),
  ];

  const wins = sortedFiltered.filter((m) => m.win).length;
  const losses = sortedFiltered.length - wins;

  return (
    <Card padding={0}>
      {/* Header */}
      <Box sx={{ p: { xs: 2, md: 3 }, pb: 2, display: 'flex', alignItems: { sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ ...d.typo.title, color: d.colors.text, mb: 0.25 }}>Games</Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Pill tone="neutral" size="sm">{sortedFiltered.length} games</Pill>
            <Pill tone="success" size="sm">{wins}W</Pill>
            <Pill tone="danger" size="sm">{losses}L</Pill>
            {sortedFiltered.length > 0 && (
              <Pill tone="outline" size="sm">
                {Math.round((wins / sortedFiltered.length) * 100)}% wins
              </Pill>
            )}
          </Stack>
        </Box>

        {useSegmented ? (
          <SegmentedControl
            options={tournamentOptions}
            value={tournamentFilter}
            onChange={setTournamentFilter}
            size="sm"
          />
        ) : (
          <TextField
            select
            size="small"
            label="Competition"
            value={tournamentFilter}
            onChange={(e) => setTournamentFilter(e.target.value)}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': { borderRadius: `${d.radius.md}px`, bgcolor: d.colors.surface },
            }}
          >
            <MenuItem value="">All ({games.matches.length})</MenuItem>
            {games.tournaments?.map((t) => {
              const count = games.matches.filter((m) => m.tournament_id === t.id).length;
              return (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.name} ({count})
                </MenuItem>
              );
            })}
          </TextField>
        )}
      </Box>

      {/* Table */}
      <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${d.colors.divider}` }}>
        <Table
          size="small"
          sx={{
            '& .MuiTableCell-root': {
              fontFamily: d.typo.fontFamily,
              fontSize: 13.5,
              color: d.colors.text,
              borderBottom: `1px solid ${d.colors.divider}`,
              py: 1.25,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <SortHeader active={sortKey === 'date'} dir={sortDir} onClick={() => handleSort('date')} minWidth={92}>
                Date
              </SortHeader>
              <SortHeader active={sortKey === 'tournament'} dir={sortDir} onClick={() => handleSort('tournament')}>
                Competition
              </SortHeader>
              <TableCell align="center" sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, borderBottom: `1px solid ${d.colors.divider}`, position: 'sticky', top: 0, zIndex: 1 }}>
                Result
              </TableCell>
              <TableCell sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, borderBottom: `1px solid ${d.colors.divider}`, position: 'sticky', top: 0, zIndex: 1 }}>
                Matchup
              </TableCell>
              <SortHeader active={sortKey === 'diff'} dir={sortDir} onClick={() => handleSort('diff')} align="center" minWidth={90}>
                Score
              </SortHeader>
              <TableCell sx={{ ...d.typo.micro, color: d.colors.textSecondary, bgcolor: d.colors.canvasAlt, borderBottom: `1px solid ${d.colors.divider}`, position: 'sticky', top: 0, zIndex: 1, minWidth: 280 }}>
                Video
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFiltered.map((m) => {
              const diff = (m.team?.score ?? 0) - (m.opp_team?.score ?? 0);
              return (
                <TableRow
                  key={m.id}
                  sx={{
                    transition: `background-color ${d.motion.fast}`,
                    '&:hover': { bgcolor: d.colors.canvasAlt },
                  }}
                >
                  <TableCell sx={{ color: d.colors.textSecondary, whiteSpace: 'nowrap', ...d.typo.tabular }}>
                    {formatDate(m.date)}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 12.5, color: d.colors.textSecondary }} noWrap>
                      {tournamentDict[m.tournament_id] || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                      <ResultDot win={m.win} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: m.win ? d.colors.success : d.colors.danger }}>
                        {m.win ? 'W' : 'L'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={m.home ? 'Home' : 'Away'}>
                        <Box sx={{ color: d.colors.textTertiary, display: 'inline-flex' }}>
                          {m.home
                            ? <HomeRoundedIcon sx={{ fontSize: 16 }} />
                            : <FlightTakeoffRoundedIcon sx={{ fontSize: 16 }} />}
                        </Box>
                      </Tooltip>
                      <Typography sx={{ fontWeight: 600, color: d.colors.text }} noWrap>
                        {m.team?.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: d.colors.textTertiary }}>vs</Typography>
                      <Typography sx={{ color: d.colors.textSecondary }} noWrap>
                        {m.opp_team?.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        ...d.typo.tabular,
                        color: d.colors.text,
                      }}
                    >
                      {m.team?.score}–{m.opp_team?.score}
                    </Typography>
                    <Typography sx={{ fontSize: 10.5, color: diff > 0 ? d.colors.success : diff < 0 ? d.colors.danger : d.colors.textTertiary, ...d.typo.tabular }}>
                      {diff > 0 ? `+${diff}` : diff}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {m.clickable ? (
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {playlistTypes.map((p) => (
                          <VideoPillButton
                            key={p.key}
                            label={shortLabel(p.key)}
                            onClick={() =>
                              openMatchVideo({
                                playerId,
                                playerName,
                                match: m,
                                playlistType: p.key,
                                label: p.label,
                                playerContext,
                              })
                            }
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: 11, color: d.colors.textTertiary }}>No video</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderTop: `1px solid ${d.colors.divider}` }}>
        <Typography sx={{ ...d.typo.caption }}>
          Click a pill → opens the Hudl playlist in a new tab.
        </Typography>
      </Box>
    </Card>
  );
}
